<script setup lang="ts">
  import JSONEditor from 'jsoneditor'
  import { onMounted, onUnmounted, ref, watch } from 'vue'
  import { Button, message } from 'ant-design-vue'
  import { CheckCircle2 } from 'lucide-vue-next'
  import { defaultMessageDurationSeconds } from '@/utils'
  import { CalculationSchema } from '../models/CalculationSchema'
  import { CalculationJsonEditorSchema } from '../services/CalculationJsonCalculationConfigurationService'
  import { CalculationsService } from '../services/CalculationsService'
  import { CalculationsTypeGuardService } from '../services/CalculationsTypeGuardService'

  interface IJsonCalculationConfigurationProps {
    calculationSchemas: Array<CalculationSchema>
    availableColumns: Array<{ value: string; label: string }>
    modelValue: {}
    placeHolder?: string
    configurationLevel: number
    editorSchema: CalculationJsonEditorSchema
    allowTypeChange: boolean
  }

  interface IJsonCalculationConfigurationEmits {
    (e: 'update:modelValue', value: {}): void
    (e: 'calculation-type-changed', type: string, configuration: {}): void
  }

  const props = defineProps<IJsonCalculationConfigurationProps>()
  const emit = defineEmits<IJsonCalculationConfigurationEmits>()

  const modelValue = ref<{}>({ ...props.modelValue })

  watch(
    modelValue,
    (newVal) => {
      emit('update:modelValue', newVal)
    },
    { deep: true }
  )

  const newApplyableCalculationType = ref<string | null>(null)

  const editorContainer = ref<HTMLElement | null>(null)
  let editor: any = null

  const initializeJSONEditor = () => {
    if (editorContainer.value) {
      editor = new JSONEditor(editorContainer.value, {
        mode: 'code',
        schema: props.editorSchema,
        onError: (err: Error) => console.error('Error:', err),
        onChange: () => {
          try {
            const newVal = editor.get()
            if (props.allowTypeChange === true) {
              onEditorChangeForCalculation(newVal)
            } else {
              onEditorChangeForField(newVal)
            }
          } catch (err: any) {
            const editorValue = editor.getText?.() ?? ''
            if (editorValue.trim() === '') {
              message.error('Editor content cannot be empty.', defaultMessageDurationSeconds)
            }
          }
        }
      })
      setJSONEditorModel(modelValue.value)
    }
  }

  const onEditorChangeForCalculation = (editorValue: any) => {
    newApplyableCalculationType.value = null
    if (typeof editorValue === 'object') {
      if (editorValue.type != null) {
        const calculationType = CalculationsService.tryGetCalculationSchemaByType(
          editorValue.type,
          props.calculationSchemas
        )
        if (
          calculationType != null &&
          calculationType.type !== CalculationsService.CUSTOM_CALCULATION_TYPE &&
          (props.configurationLevel === 1 ||
            !CalculationsTypeGuardService.supportsChildCalculations(calculationType))
        ) {
          newApplyableCalculationType.value = calculationType.type
        }
      }
      modelValue.value = editorValue
    }
  }

  const onEditorChangeForField = (editorValue: any) => {
    modelValue.value = editorValue
  }

  const setJSONEditorModel = (value: {}) => {
    if (editor != null) {
      newApplyableCalculationType.value = null
      editor.set(value)
      if (editor.getMode() === 'tree') editor.expandAll()
    }
  }

  const onApplyNewCalculationType = () => {
    if (newApplyableCalculationType != null) {
      emit('calculation-type-changed', newApplyableCalculationType.value as string, modelValue)
    }
  }

  onMounted(() => {
    initializeJSONEditor()
  })

  onUnmounted(() => {
    if (editor) {
      editor.destroy()
    }
  })
</script>

<template>
  <div class="flex flex-col">
    <div v-if="props.allowTypeChange === true" class="mb-3 flex justify-end">
      <a-tooltip>
        <template #title>
          {{ `Apply Calculation type: ${newApplyableCalculationType}` }}
        </template>
        <Button type="primary" :disabled="newApplyableCalculationType == null" @click="onApplyNewCalculationType">
          <span class="ant-button-flex-content">
            <CheckCircle2 class="h-4 w-4" />
            {{ 'Apply Calculation Type' }}
          </span>
        </Button>
      </a-tooltip>
    </div>
    <div ref="editorContainer" style="min-height: 500px; height: 500px"></div>
  </div>
</template>
